import React, { useState, Suspense, useEffect } from "react";
import {Link, NavLink, Routes, Route, useLocation} from "react-router-dom";
import {
  Tag,
  Space,
  Switch as SwitchField,
  Avatar,
  Menu,
  Drawer,
  Button,
  Breadcrumb,
  Layout,
  Select,
} from "antd";
import { PageHeader } from '@ant-design/pro-layout';
import systemRoutes from "../routes/BusinessRoutes";
import ContentSuspenseComponent from "../../components/ContentSuspenseComponent";
import ErrorPage from "../../pages/ErrorPage";
import DependenciesComponent from "../../components/DependenciesComponent";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {faHome, faUser} from "@fortawesome/free-solid-svg-icons";
import config from "../config.json";
import {useAxios} from "../../hooks/hooks";
import {useKeycloak} from "@react-keycloak/web";

const { Option } = Select;
const { SubMenu } = Menu;
const { Content } = Layout;
/**
 * DashboardLayout component acts as the main layout for the application's dashboard page.
 * It incorporates navigation, user interactions, and key page elements using Ant Design's components.
 *
 * @component
 *
 * @param {Object} props - React props passed down to the component.
 *
 * @returns {JSX.Element} The rendered component.
 *
 * @description
 * The `DashboardLayout` is responsible for rendering the main layout within the dashboard view,
 * including header, navigation drawer, breadcrumbs, and managing user session and error handling.
 * Utilizes state to manage asynchronous data and user interactions such as logins and page transitions.
 *
 * @example
 * return (
 *   <DashboardLayout>
 *     { Include children or routes within DashboardLayout as needed.}
*   </DashboardLayout>
* )
*/
function DashboardLayout(props) {
  const axios = useAxios();

  const location = useLocation();
  const [error, setError] = useState(null);
  const [online] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [endpoints, setEndpoints] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [drawerVisibility, setDrawerVisibility] = useState(false);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const {initialized, keycloak} = useKeycloak();
  const [projectMode, setProjectMode] = useState(false);

  const showDrawer = () => {
    setDrawerVisibility(true);
  };
  const hideDrawer = () => {
    setDrawerVisibility(false);
  };
  const logout = () => {
    if (initialized) {
      keycloak.logout().then(() => {});
    }
  }
  // const toggleProjectMode = () => {
  //   const mode = !projectMode;
  //   setProjectMode(mode);
  // };


  const getEndpoints = async () => {
    try {
      const response = await axios.current.get("endpoints");
      const data = response.data;

      setUserProfile(data.profile);
      setEndpoints(data.endpoints);
      setLoaded(true);
    } catch (e) {
      setError(e.message);
    }
  };

  const getProjects = async () => {
    try {
      const response = await axios.current.get("projects?page_size=250&page=1");
      const data = response.data;
      setProjects(data.rows);
    } catch (e) {
      setError(e.message);
    }
  };

  useEffect(() => {
    getEndpoints();
  }, []); // empty array required to prevent infinte loop

  useEffect(() => {

    if (endpoints === null) return;
    if (undefined === endpoints.projects) return;
    getProjects();
  }, [endpoints]);
  useEffect(() => {
    // eslint-disable-next-line no-restricted-globals
    var crumbs = location.pathname.split("/");
    crumbs.shift();
    const crumbs_len = crumbs.length;
    var bcrumbs = [{
      title: (
          <Link to="/">
            <FontAwesomeIcon icon={faHome} />
          </Link>
      ),
    }];
    var link = "/";
    for (var c = 0; c < crumbs_len; c++) {
      link += crumbs[c] + "/";
      bcrumbs.push(({
        title: <Link to={link}>{crumbs[c]}</Link>,
      }))
      // bcrumbs.push({
      //   label: crumbs[c],
      //   href: link,
      // });
    }
    setBreadcrumbs(bcrumbs);
  }, [location]);

  return (
    <>
      <Layout className="layout" style={{ minHeight: "100vh" }}>
        <Drawer
          title={
            <Space>
              <Avatar
                key="2"
                size="small"
                icon={<FontAwesomeIcon icon={faUser} className="fa-fw" />}
                style={{ marginRight: "0px" }}
                src={userProfile ? userProfile.avatar_user : "null"}
              />
              <b key="1">{userProfile ? userProfile.first_name_user : ""}</b>
            </Space>
          }
          placement="right"
          closable={true}
          onClose={hideDrawer}
          open={drawerVisibility}

          // bodyStyle={{ padding: "0px" }}
        >
          <Menu mode="inline" style={{ border: "none" }}>
            {!error &&
              loaded &&
              Object.keys(userProfile.endpoints).map((e, i) => (
                <SubMenu
                  key={i}
                  title={
                    <span>
                      <FontAwesomeIcon icon={userProfile.endpoints[e].icon || "table"} className="fa-fw"/>
                      &nbsp;
                      {userProfile.endpoints[e].duty}
                    </span>
                  }
                >
                  {Object.keys(userProfile.endpoints[e].namespaces).map(
                    (n, j) => (
                      <Menu.Item
                        key={j}
                        icon={
                          <FontAwesomeIcon
                            icon={
                              userProfile.endpoints[e].namespaces[n].icon ||
                              "table"
                            }
                            className="fa-fw"
                          />
                        }
                      >
                        <NavLink
                          replace={true}
                          to={
                            "/dashboard/" +
                            userProfile.endpoints[e].namespaces[n].code
                          }
                        >
                          &nbsp;{userProfile.endpoints[e].namespaces[n].name}
                        </NavLink>
                      </Menu.Item>
                    )
                  )}
                </SubMenu>
              ))}
            <Menu.Item>
              <Button
                type="primary"
                block
                icon={<FontAwesomeIcon icon={"sign-out-alt"} className="fa-fw" />}
                onClick={logout}
              >
                Logout
              </Button>
            </Menu.Item>
          </Menu>
        </Drawer>
        <PageHeader
          ghost={false}
          backIcon={
            <Button
              key="1"
              icon={
                drawerVisibility ? (
                  <FontAwesomeIcon icon="bars"/>
                ) : (
                  <FontAwesomeIcon icon="bars"/>
                )
              }
            />
          }
          title={
            <Link replace={true} to="/dashboard">
              {config.title}&nbsp;<Tag color="blue">{config.version}</Tag>
            </Link>
          }
          extra={[
            <DependenciesComponent
              key="deps"
              mode="hide"
              dependencies={["coc", "projects"]}
              endpoints={endpoints ? endpoints : {}}
            >
              <Space key="space">
                <span key="span">
                  <b>
                    <FontAwesomeIcon
                      style={{
                        color: online ? "#1890ff" : "#bfbfbf",
                      }}
                      className="fa-fw"
                      icon="wifi"
                    />
                    &nbsp;Project Mode
                  </b>
                </span>
                <Button type="primary" key="new-project-button">
                  <NavLink replace={true} to={"/dashboard/projects/new"}>
                    <FontAwesomeIcon icon={{prefix: 'fas', iconName: 'plus'}}/>
                    &nbsp; Project
                  </NavLink>
                </Button>
                <Button
                  key="existing-project-button"
                  onClick={getProjects}
                >
                  <FontAwesomeIcon icon="sync" />
                  &nbsp; Projects
                </Button>
                <Select
                    showSearch
                    variant={"borderless"}
                  key="select"
                    optionFilterProp="value"
                  onChange={setCurrentProject}
                  placeholder="Choose a project"
                >
                  {projects.map((p, i) => {
                    return (
                      <Option key={i} value={p.id_project}>
                        #{p.id_project}&nbsp;{p.place_project}
                      </Option>
                    );
                  })}
                </Select>
                <SwitchField
                  checkedChildren="ON"
                  unCheckedChildren="OFF"
                  key="switch"
                  checked={projectMode}
                  onChange={setProjectMode}
                  disabled={!currentProject}
                />
                <Button key="button" type="primary">
                  <NavLink replace={true} to={"/dashboard/"}>
                    <FontAwesomeIcon icon="tachometer-alt" />
                    &nbsp; Panel
                  </NavLink>
                </Button>
              </Space>
            </DependenciesComponent>,
          ]}
          onBack={showDrawer}
        />
        <Content style={{ padding: "0 24px" }}>
          <Breadcrumb style={{ margin: "24px 0" }} items={breadcrumbs}>

          </Breadcrumb>
          <div
            className="site-layout-content"
            style={{ padding: "24px", background: "white" }}
          >
            {error && <ErrorPage error={error} />}
            {loaded && !error && (

                <Suspense fallback={<ContentSuspenseComponent/>}>
                  {/*Before*/}
                  {/*<Routes children={systemRoutes.map((route, i) => {*/}

                  {/*  return <Route*/}
                  {/*      key={i}*/}
                  {/*      exact={true}*/}
                  {/*      index*/}
                  {/*      path={`${route.path}`}*/}
                  {/*      element={< route.component*/}
                  {/*          home="/dashboard/"*/}
                  {/*          endpoints={endpoints}*/}
                  {/*          projectMode={projectMode}*/}
                  {/*          currentProject={currentProject}*/}
                  {/*          userProfile={userProfile}*/}
                  {/*      />}*/}
                  {/*  />*/}
                  {/*})}>*/}
                  {/*After*/}
                  <Routes>
                    {systemRoutes.map((route, i) => (
                        <Route
                            key={i}
                            exact={true}
                            index
                            path={`${route.path}`}
                            element={<route.component
                                home="/dashboard/"
                                endpoints={endpoints}
                                projectMode={projectMode}
                                currentProject={currentProject}
                                userProfile={userProfile}
                            />}
                        />
                    ))}
                    {/*<Route path="/" element={<DashboardLayout />} />*/}

                    {/*<Route component={NotFoundPage}/>*/}
                  </Routes>
                </Suspense>
            )}
          </div>
        </Content>
      </Layout>
    </>
  );
}

export default DashboardLayout;
